import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";

import { EnergyPulse } from "../brand/EnergyPulse";

/**
 * The three states every data-backed screen has to render: loading, empty, and failed.
 *
 * Keeping them in one file means a new screen gets all three for free and they cannot
 * drift apart visually — which is exactly what happens when each screen rolls its own.
 */

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        textAlign: "center",
        paddingBlock: 8,
        paddingInline: 3,
      }}
    >
      {children}
    </Box>
  );
}

export function LoadingState({ label }: { label?: string }) {
  const { t } = useTranslation();
  return (
    <Frame>
      <CircularProgress size={28} thickness={4} />
      <Typography variant="T3" sx={{ color: "text.muted" }}>
        {label ?? t("common.loading")}
      </Typography>
    </Frame>
  );
}

export function EmptyState({ message }: { message: string }) {
  return (
    <Frame>
      <EnergyPulse size={96} opacity={0.35} blur={2} />
      <Typography variant="T2" sx={{ color: "text.secondary", maxWidth: 320 }}>
        {message}
      </Typography>
    </Frame>
  );
}

export function ErrorState({
  message,
  onRetry,
}: {
  message?: string;
  onRetry?: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Frame>
      <Typography variant="H3" sx={{ color: "text.primary" }}>
        {t("common.somethingWentWrong")}
      </Typography>
      {message ? (
        <Typography variant="T3" sx={{ color: "text.muted", maxWidth: 320 }}>
          {message}
        </Typography>
      ) : null}
      {onRetry ? (
        <Button variant="outlined" onClick={onRetry} sx={{ marginTop: 1 }}>
          {t("common.retry")}
        </Button>
      ) : null}
    </Frame>
  );
}
